import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import { UndoOutlined } from '@ant-design/icons';
import { CentralTeamDashboardFilter } from '../../../shared/ScreeningServiceTypes';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	Batch,
	CandidateScreeningProgressSummaryFilter,
} from '../../../shared/VolunteerApplicationServiceTypes';
import RegionsDropdown from '../../../components/RegionsDropdown';
import ZonesDropdown from '../../../components/ZonesDropdown';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface RegionOption {
	value: number | undefined;
	label: string | undefined;
}

interface ZoneOption {
	value: string;
	label: string;
}

interface FilterProps {
	open: boolean;
	onClose: () => void;
	filterValue: CentralTeamDashboardFilter;
	onFilterChange: (value: CentralTeamDashboardFilter) => void;
	onScreeningDashboardFilterChange: (
		value: CandidateScreeningProgressSummaryFilter
	) => void;
}

export const FilterCentralTeamDashboard = ({
	open,
	onClose,
	filterValue,
	onFilterChange,
	onScreeningDashboardFilterChange,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const { srcmId } = useAuth();
	const { batchId } = useBatchId();
	const lengthyLanguages = ['ta', 'ma', 'te', 'ka'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [zoneIds, setZoneIds] = useState<number[]>([]);
	const [selectedZones, setSelectedZones] = useState<ZoneOption[]>([]);
	const [zoneNames, setZoneNames] = useState<Array<string> | undefined>();
	const [regions, setRegions] = useState<RegionOption>();
	const [selectedBatchId, setSelectedBatchId] = useState<number>();

	useEffect(() => {
		const filterZoneIds = filterValue.zoneId ?? [];
		setZoneIds(filterZoneIds);

		setSelectedZones((prev) => {
			const prevById = new Map<number, { value: string; label: string }>(
				prev.map((z) => [Number(z.value), z])
			);

			return filterZoneIds.map((id) => {
				const existing = prevById.get(id);
				return existing ?? { value: String(id), label: String(id) }; // fallback only if we don't know label
			});
		});

		setZoneNames(undefined);

		setRegions(
			filterValue.regionId !== undefined
				? { value: filterValue.regionId[0], label: undefined }
				: undefined
		);
	}, [filterValue]);

	const handleZoneChange = useCallback(
		(value: Array<{ value: string; label: string }>) => {
			setSelectedZones(value);

			const zoneId = value
				.map((z) => Number(z.value))
				.filter((n): n is number => Number.isFinite(n));

			setZoneIds(zoneId);
			setZoneNames(value.map((z) => z.label));
		},
		[]
	);

	const handleRegionsChange = useCallback((value: RegionOption) => {
		setZoneIds([]);
		setSelectedZones([]);
		setZoneNames(undefined);
		setRegions(value);
	}, []);

	const handleResetFilters = useCallback(() => {
		const effectiveBatchId = selectedBatchId ?? batchId;
		if (!effectiveBatchId) return;

		setZoneIds([]);
		setSelectedZones([]);
		setZoneNames(undefined);
		setRegions(undefined);
		onFilterChange({ batchId: [effectiveBatchId] });
		onScreeningDashboardFilterChange({});

		onClose();
	}, [
		selectedBatchId,
		batchId,
		onClose,
		onFilterChange,
		onScreeningDashboardFilterChange,
	]);

	const handleFilterButtonClick = useCallback(() => {
		const effectiveBatchId = selectedBatchId ?? batchId;
		if (!effectiveBatchId) return;

		const appliedFilter: CentralTeamDashboardFilter = {
			batchId: [effectiveBatchId],
		};

		if (regions?.value) {
			appliedFilter.regionId = [regions.value];
		}

		if (zoneIds.length) {
			appliedFilter.zoneId = zoneIds;
		}

		onFilterChange(appliedFilter);

		const appliedScreeningFilter: CandidateScreeningProgressSummaryFilter = {};
		if (regions?.value) appliedScreeningFilter.regionId = regions.value;
		if (zoneNames?.length) appliedScreeningFilter.zoneNames = zoneNames;
		onScreeningDashboardFilterChange(appliedScreeningFilter);

		onClose();
	}, [
		selectedBatchId,
		batchId,
		regions?.value,
		zoneIds,
		zoneNames,
		onClose,
		onFilterChange,
		onScreeningDashboardFilterChange,
	]);

	useEffect(() => {
		const selectedBatchDetails = sessionStorage.getItem('SELECTED_BATCH');

		if (selectedBatchDetails) {
			const parsedData: Batch = JSON.parse(selectedBatchDetails) as Batch;
			setSelectedBatchId(parsedData.id);
		} else {
			setSelectedBatchId(batchId);
		}
	}, [batchId]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterDashboard')}
			</div>
			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByRegion')}
						</Text>
						<RegionsDropdown
							functionarySrcmId={srcmId}
							value={regions}
							onChange={handleRegionsChange}
							getRegionChange={true}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByZone')}
						</Text>
						<ZonesDropdown
							functionarySrcmId={srcmId}
							value={selectedZones}
							onChange={handleZoneChange}
							disabled={!regions?.value}
							multiple={true}
							regionId={regions?.value}
							key={regions?.value}
							getZoneChange={true}
						/>
					</div>
				</div>
			</div>
			<div className="flex justify-center">
				<Button
					className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}

						<UndoOutlined className="ml-2" />
					</span>
				</Button>
			</div>
			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-center gap-2'
				} mt-4 ant-modal-footer`}
			>
				<Button
					className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
